import { useEffect, useState, useCallback } from "react";
import { ExternalLink, Check, X, ChevronDown, ChevronUp, Loader2, Copy } from "lucide-react";
import { getDiscoveryCandidates, approveCandidate, rejectCandidate, updateCandidate, findSupportingEvidence } from "@/lib/apiClient";
import AdminNav from "@/components/AdminNav";
import { toast } from "sonner";

const TABS = [
  { label: "Ready for Review", value: "new" },
  { label: "Possible", value: "possible" },
  { label: "Duplicates", value: "duplicate" },
  { label: "Published", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const REJECT_REASONS = [
  "Weak evidence", "Not materially transformative", "Generic AI adoption",
  "Duplicate", "Unreliable source", "Insufficient information", "Not relevant", "Other",
];

const strengthStyle = { HIGH: "bg-brlime text-ink", MEDIUM: "bg-bryellow text-ink", LOW: "bg-secondary text-ink/70" };

function EvidencePanel({ c }) {
  return (
    <div className="bg-secondary/50 border-2 border-ink p-4">
      <p className="eyebrow text-ink/50 mb-2">Source Evidence</p>
      <div className="text-sm font-bold">{c.source_organisation} <span className="font-normal text-ink/50">· {c.source_type}</span></div>
      {c.publication_date && <div className="text-xs text-ink/50">{c.publication_date}</div>}
      <div className="mt-2 text-sm text-ink/80"><span className="text-ink/50">Original title:</span> {c.source_title || c.headline}</div>
      {c.evidence_quote && <blockquote className="mt-3 text-sm italic border-l-4 border-ink pl-3 text-ink/70">“{c.evidence_quote}”</blockquote>}
      {c.key_metric && <div className="mt-3 text-sm"><span className="text-ink/50">Reported metric:</span> <strong>{c.key_metric}</strong> {c.metric_context}</div>}
      {(c.technology_product || c.technology_provider) && (
        <div className="mt-2 text-sm"><span className="text-ink/50">Technology:</span> {c.technology_product}{c.technology_provider ? ` (${c.technology_provider})` : ""}</div>
      )}
      <a href={c.source_url} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-1.5 mt-3 font-bold text-sm text-cobalt">
        View original source <ExternalLink size={14} className="arrow-move" />
      </a>
      {c.extra_sources?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-ink/15">
          <p className="text-xs text-ink/50 mb-1">Supporting sources</p>
          {c.extra_sources.map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block text-xs text-cobalt truncate">{s.source_name} · {s.url}</a>
          ))}
        </div>
      )}
    </div>
  );
}

function DraftPanel({ c, edit, setEdit }) {
  const F = (k, label, area) => (
    <div>
      <label className="eyebrow text-ink/50 block mb-1">{label}</label>
      {area
        ? <textarea defaultValue={c[k]} onChange={(e) => setEdit((p) => ({ ...(p || {}), [k]: e.target.value }))} className="w-full p-2 border-2 border-ink/25 text-sm min-h-[64px] bg-white" />
        : <input defaultValue={c[k]} onChange={(e) => setEdit((p) => ({ ...(p || {}), [k]: e.target.value }))} className="w-full h-9 px-2 border-2 border-ink/25 text-sm bg-white" />}
    </div>
  );
  return (
    <div className="border-2 border-ink p-4 space-y-3">
      <p className="eyebrow text-ink/50">AI YAI YAI Draft <span className="text-ink/40">(edit before publishing)</span></p>
      {F("headline", "Headline")}
      {F("summary", "Summary", true)}
      <div className="grid grid-cols-2 gap-3">
        {F("company", "Company")}{F("country", "Country")}
        {F("industry", "Industry")}{F("business_type", "Business Type")}
        {F("key_metric", "Key Metric")}{F("metric_context", "Metric Context")}
      </div>
      {F("before_ai", "Before AI", true)}
      {F("what_changed", "What Changed", true)}
      {F("result", "The Result", true)}
      {F("why_this_matters_headline", "Why This Matters — Headline")}
      {F("why_this_matters_body", "Why This Matters — Body", true)}
      {c.opportunity_concept && <div className="text-xs text-ink/60 bg-bryellow/40 p-2 border border-ink/15"><strong>Opportunity concept:</strong> {c.opportunity_concept}</div>}
    </div>
  );
}

function CandidateCard({ c, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(c.image || "");
  const [edit, setEdit] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  const approve = async (publish) => {
    setBusy(true);
    try {
      if (edit) await updateCandidate(c.id, edit);
      const res = await approveCandidate(c.id, publish, image || undefined);
      toast.success(publish ? `Published: ${res.slug}` : `Saved as draft: ${res.slug}`);
      onChanged();
    } catch (e) { toast.error("Approve failed"); } finally { setBusy(false); }
  };
  const doReject = async (reason) => {
    setBusy(true);
    try { await rejectCandidate(c.id, reason); toast.message(`Rejected: ${reason}`); onChanged(); }
    catch (e) { toast.error("Reject failed"); } finally { setBusy(false); setRejecting(false); }
  };

  const isActionable = c.status === "new" || c.status === "possible";

  return (
    <div className="border-2 border-ink bg-white" data-testid={`candidate-${c.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span className={`eyebrow px-2 py-0.5 ${strengthStyle[c.opportunity_strength] || strengthStyle.MEDIUM}`}>{c.opportunity_strength} opportunity</span>
              {c.company_scale && (
                <span className={`eyebrow px-2 py-0.5 ${c.is_sme ? "bg-cobalt text-white" : "border border-ink/20 text-ink/60"}`}>{c.is_sme ? "SME" : c.company_scale}</span>
              )}
              <span className="eyebrow px-2 py-0.5 border border-ink/20">Evidence: {c.evidence_strength || "—"}</span>
              <span className="text-xs text-ink/50">{[c.industry, c.country, c.business_type].filter(Boolean).join(" · ")}</span>
            </div>
            <h3 className="font-display font-bold text-lg leading-tight">{c.company || "Unknown"} — {c.headline}</h3>
            <p className="text-sm text-ink/60 mt-1 line-clamp-2">{c.summary}</p>
            {c.key_metric && <p className="mt-2 text-sm"><strong className="font-display text-base">{c.key_metric}</strong> <span className="text-ink/50">{c.metric_context}</span></p>}
            {c.status === "duplicate" && c.dup_of && (
              <p className="mt-2 text-xs text-brorange inline-flex items-center gap-1"><Copy size={12} /> Duplicate of {c.dup_of.company} ({c.dup_of.reason})</p>
            )}
            {c.status === "rejected" && c.reject_reason && <p className="mt-2 text-xs text-ink/50">Rejected: {c.reject_reason}</p>}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-ink/50">
              <span>{c.source_organisation} · {c.source_type}</span>
              {c.extra_sources?.length > 0 && <span className="text-cobalt">+{c.extra_sources.length} source(s)</span>}
            </div>
          </div>
          <button onClick={() => setOpen((o) => !o)} className="shrink-0 h-9 w-9 flex items-center justify-center border-2 border-ink hover:bg-secondary" data-testid={`candidate-expand-${c.id}`}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <a href={c.source_url} target="_blank" rel="noreferrer" className="btn-tactile inline-flex items-center gap-1.5 border-2 border-ink px-3 h-9 text-sm font-semibold hover:bg-secondary" data-testid={`candidate-source-${c.id}`}>
            <ExternalLink size={14} /> View Source
          </a>
          {isActionable && (
            <>
              <button onClick={() => setOpen(true)} className="btn-tactile inline-flex items-center gap-1.5 border-2 border-ink px-3 h-9 text-sm font-semibold hover:bg-secondary">Preview</button>
              <button onClick={() => approve(true)} disabled={busy} data-testid={`candidate-approve-${c.id}`} className="btn-tactile inline-flex items-center gap-1.5 bg-ink text-white px-3 h-9 text-sm font-bold hover:bg-cobalt disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve & Publish
              </button>
              <button onClick={() => approve(false)} disabled={busy} className="btn-tactile inline-flex items-center gap-1.5 border-2 border-ink px-3 h-9 text-sm font-semibold hover:bg-bryellow disabled:opacity-60">Save as Draft</button>
              <button onClick={() => setRejecting((r) => !r)} disabled={busy} data-testid={`candidate-reject-${c.id}`} className="btn-tactile inline-flex items-center gap-1.5 border-2 border-ink px-3 h-9 text-sm font-semibold hover:bg-brorange hover:text-white disabled:opacity-60">
                <X size={14} /> Reject
              </button>
              <button onClick={async () => { setBusy(true); try { const r = await findSupportingEvidence(c.id); toast.success(`Evidence: ${r.extra_sources.length} source(s) · ${r.evidence_strength}`); onChanged(); } catch { toast.error("Search failed"); } finally { setBusy(false); } }} disabled={busy} data-testid={`candidate-evidence-${c.id}`} className="btn-tactile inline-flex items-center gap-1.5 border-2 border-ink px-3 h-9 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
                Find Supporting Evidence
              </button>
            </>
          )}
          {c.status === "approved" && c.published_slug && (
            <a href={`/case/${c.published_slug}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-cobalt">View published case →</a>
          )}
        </div>

        {rejecting && (
          <div className="mt-3 flex flex-wrap gap-2 p-3 border-2 border-dashed border-ink/30" data-testid={`reject-reasons-${c.id}`}>
            <span className="eyebrow text-ink/50 w-full">Reject reason</span>
            {REJECT_REASONS.map((r) => (
              <button key={r} onClick={() => doReject(r)} className="px-3 h-8 border-2 border-ink text-xs font-semibold hover:bg-brorange hover:text-white">{r}</button>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="border-t-2 border-ink p-4 bg-secondary/30">
          <p className="text-xs text-ink/50 mb-3">AI qualification: <strong>{c.verdict}</strong> — {c.qualification_reason}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <EvidencePanel c={c} />
            <DraftPanel c={c} edit={edit} setEdit={setEdit} />
          </div>
          {isActionable && (
            <div className="mt-4">
              <label className="eyebrow text-ink/50 block mb-1">Image URL for the published card (optional)</label>
              <input value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://…" className="w-full h-9 px-2 border-2 border-ink/25 text-sm bg-white" data-testid={`candidate-image-${c.id}`} />
              <button onClick={() => approve(true)} disabled={busy} className="btn-tactile inline-flex items-center gap-1.5 bg-ink text-white px-4 h-10 text-sm font-bold mt-3 hover:bg-cobalt disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve & Publish
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminQueue() {
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smeOnly, setSmeOnly] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getDiscoveryCandidates(tab).then((d) => { setItems(d); setLoading(false); });
  }, [tab]);
  useEffect(load, [load]);

  const shown = smeOnly ? items.filter((c) => c.is_sme) : items;

  return (
    <div>
      <AdminNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="eyebrow text-ink/60">Editorial control</p>
        <h1 className="font-display font-extrabold text-4xl display-tight uppercase">Discovery Queue</h1>
        <p className="text-sm text-ink/60 mt-1">AI YAI YAI did the research and drafting. Your job is editorial judgement and final approval. Small & medium businesses are shown first.</p>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          {TABS.map((t) => (
            <button key={t.value} onClick={() => setTab(t.value)} data-testid={`queue-tab-${t.value}`}
              className={`btn-tactile px-4 h-10 border-2 border-ink font-semibold text-sm ${tab === t.value ? "bg-ink text-white" : "bg-white hover:bg-bryellow"}`}>
              {t.label}
            </button>
          ))}
          <button onClick={() => setSmeOnly((s) => !s)} data-testid="sme-only-toggle"
            className={`btn-tactile ml-auto px-4 h-10 border-2 border-ink font-semibold text-sm ${smeOnly ? "bg-cobalt text-white" : "bg-white hover:bg-bryellow"}`}>
            {smeOnly ? "✓ SME only" : "SME only"}
          </button>
        </div>

        <div className="mt-6 space-y-4" data-testid="candidate-list">
          {loading ? (
            <div className="text-center py-12 text-ink/40">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="border-2 border-ink p-10 text-center">
              <h3 className="font-display font-extrabold text-xl">Nothing here.</h3>
              <p className="text-ink/60 mt-1 text-sm">{smeOnly ? "No small/medium-business candidates in this view yet — run discovery to find more." : "Run the Discovery Engine from the dashboard to populate this queue."}</p>
            </div>
          ) : shown.map((c) => <CandidateCard key={c.id} c={c} onChanged={load} />)}
        </div>
      </div>
    </div>
  );
}
